import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson, deleteJson } from '../lib/api';

export interface ContactFilters {
  search?: string;
  role?: string;
  status?: string;
  organizationId?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  title?: string;
  insuranceRole?: string;
  status?: string;
  organizationId?: string;
  organizationName?: string;
  tags?: string[];
  lastActivityAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ContactDetail extends Contact {
  emails?: { type: string; value: string }[];
  phones?: { type: string; value: string }[];
  addresses?: { type: string; street: string; city: string; state: string; zip: string }[];
  customFields?: Record<string, string>;
  notes?: string;
  source?: string;
}

export interface ContactsResponse {
  contacts: Contact[];
  total: number;
  limit: number;
  offset: number;
}

function buildQueryString(filters?: ContactFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.role) params.set('role', filters.role);
  if (filters.status) params.set('status', filters.status);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);
  if (filters.tag) params.set('tag', filters.tag);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useContacts(filters?: ContactFilters) {
  return useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => fetchJson<ContactsResponse>(`/api/contacts${buildQueryString(filters)}`),
  });
}

export function useContact(id: string | null) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => fetchJson<ContactDetail>(`/api/contacts/${id}`),
    enabled: !!id,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ContactDetail>) => postJson<ContactDetail>('/api/contacts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContactDetail> }) =>
      putJson<ContactDetail>(`/api/contacts/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contact', variables.id] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteJson(`/api/contacts/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}
